import { OptimizedImage } from "@/components/OptimizedImage";
import { useLanguage } from "@/context/LanguageContext";
import { Seo } from "@/components/Seo";
import { SectionHeading, Eyebrow, QuoteButton } from "@/components/shared";
import { SITE } from "@/config/site";
import { IMG } from "@/config/images";
import { ShieldCheck, MapPin, Users, Car, HeartHandshake } from "lucide-react";

const VALUES = [
  { icon: ShieldCheck, title: "Honest quotes", desc: "Compare published tariffs and confirm the full trip quotation before booking." },
  { icon: Car, title: "Right vehicle for the trip", desc: "From sedans to Tempo Travellers, we match the vehicle to your journey." },
  { icon: Users, title: "Group travel ready", desc: "Comfortable Tempo Travellers for families, pilgrimages and corporate groups." },
  { icon: HeartHandshake, title: "Local knowledge", desc: "Routes across Satna, Maihar, Rewa, Khajuraho, Chitrakoot and Central India." },
];

export default function About() {
  const { t, cityName } = useLanguage();
  return (
    <>
      <Seo
        title={t("About Jitendra Singh Tour Operator Service | Satna")}
        description={t("Meet our Satna-based taxi and Tempo Traveller operator. Learn how we arrange car hire, cab booking and local, outstation or group travel services.")}
        image={IMG.forestRoad}
      />
      <section className="bg-[#071426]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16">
          <Eyebrow className="text-amber-400">{t("About Us")}</Eyebrow>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white">{t("Jitendra Singh Tour Operator Service")}</h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-slate-300">{t("Jitendra Singh Tour Operator Service provides taxi and Tempo Traveller services for local, outstation and group travel. We arrange car hire and cab booking from our Satna base, with the route, vehicle availability and fare confirmed before travel.")}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="grid gap-10 lg:grid-cols-2 items-start">
          <div className="prose max-w-none">
            <SectionHeading eyebrow={t("Who we are")} title={t("Travel made simple from Satna")} />
            <div className="mt-4 space-y-4 text-base leading-relaxed text-slate-600">
              <p>{t("{brand} is based in {address}. We help individuals, families and groups travel comfortably — whether it's a quick station drop in Satna, a temple visit to Maihar or Chitrakoot, sightseeing in Khajuraho, or a multi-day tour by Tempo Traveller.", { brand: SITE.brand, address: cityName(SITE.addressShort) })}</p>
              <p>{t("Our focus is simple: clean vehicles, experienced drivers, and clear vehicle tariffs. You tell us your trip; we suggest the right vehicle and confirm a quotation including the applicable charges. There are no online payments or accounts to set up — just call, WhatsApp or send an enquiry.")}</p>
              <p>{t("We serve a wide network across Central India. These are service areas and destinations we cover from our Satna base; we do not claim a separate office or standing fleet in every town. For longer-distance destinations, we arrange vehicles on a prebooked basis.")}</p>
            </div>
            <div className="mt-6"><QuoteButton testId="about-quote-button">{t("Request a Quote")}</QuoteButton></div>
          </div>
          <div className="overflow-hidden rounded-2xl shadow-xl"><OptimizedImage src={IMG.forestRoad} sizes="(min-width: 1024px) 600px, calc(100vw - 32px)" alt={t("Scenic road through central India representing travel from Satna")} className="w-full object-cover" /></div>
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v) => (
            <div key={v.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={`about-value-${v.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50 text-amber-600"><v.icon className="h-5 w-5" aria-hidden="true" /></span>
              <h3 className="mt-4 text-base font-bold text-slate-900">{t(v.title)}</h3>
              <p className="mt-2 text-sm text-slate-600">{t(v.desc)}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
          <MapPin className="h-5 w-5 text-amber-600 shrink-0" aria-hidden="true" />
          <span>{t("Based in {address}. Call or WhatsApp {phone} for bookings and quotes.", { address: cityName(SITE.addressShort), phone: SITE.phonePrimary.display })}</span>
        </div>
      </section>
    </>
  );
}
